import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { PermissionGroupAssignedUserDetailsParams } from 'src/apis/authorization-service/types';
import type { AuthorizationServiceRequest } from 'src/apis/authorization-service/use-authorization-service';
import {
  useOrganisationAssignedLegacyPermissions,
  type OrganisationAssignedLegacyPermissionsRequest,
} from 'src/apis/authorization-service/use-legacy-permissions';
import { usePermissionGroup } from 'src/apis/authorization-service/use-permission-group';
import { usePermissionGroupAssignedUsers } from 'src/apis/authorization-service/use-permission-group-assigned-users';
import { usePermissionGroups } from 'src/apis/authorization-service/use-permission-groups';
import { usePermissions } from 'src/apis/authorization-service/use-permissions';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { usePageParams } from 'src/pages/settings/permission-groups/details/use-page-params';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(() => import('./page'));

type Props = {
  authorizationService: Successful<AuthorizationServiceRequest>;
};

export const EditPermissionGroup: FC<Props> = ({ authorizationService }) => {
  const permissions = usePermissions({ authorizationService });
  const permissionGroups = usePermissionGroups({
    authorizationService,
  });
  const { permissionGroupId } = useParams<{ permissionGroupId: string }>();
  const permissionGroup = usePermissionGroup({
    permissionGroups,
    permissionGroupId,
  });

  const pageParams = usePageParams();
  const assignedUsersParams = useMemo(
    (): PermissionGroupAssignedUserDetailsParams => ({
      limit: pageParams.value.pageSize,
      offset: pageParams.value.start,
    }),
    [pageParams.value]
  );

  const assignedUsers = usePermissionGroupAssignedUsers({
    assignedUsersLink: permissionGroup?.response?.data._links.assignedUsers,
    params: assignedUsersParams,
  });

  const organisationLegacyPermissions =
    useOrganisationAssignedLegacyPermissions({ authorizationService });

  if (!permissions) throw new Forbidden();
  if (!permissionGroups) throw new Forbidden();
  if (!permissionGroup) throw new Forbidden();
  const isNewFlowEnabled = authorizationService.response.data.isNewFlowEnabled;
  if (
    !permissions.response ||
    !permissionGroups.response ||
    !permissionGroup.response ||
    (!isNewFlowEnabled && !organisationLegacyPermissions?.response) ||
    assignedUsers?.response === null
  )
    return <PageSpinner />;

  return (
    <Page
      organisationLegacyPermissions={
        isNewFlowEnabled
          ? undefined
          : (organisationLegacyPermissions as Successful<OrganisationAssignedLegacyPermissionsRequest>)
      }
      permissions={permissions}
      permissionGroup={permissionGroup}
      assignedUsers={assignedUsers}
    />
  );
};
